import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as gitlabApi from '@/api/gitlab.api';

/**
 * Query keys for GitLab
 */
export const gitlabKeys = {
  all: ['gitlab'] as const,
  status: () => [...gitlabKeys.all, 'status'] as const,
  projects: () => [...gitlabKeys.all, 'projects'] as const,
  projectList: (page: number, perPage: number) =>
    [...gitlabKeys.projects(), { page, perPage }] as const,
};

/**
 * Fetch GitLab connection status
 */
/** enabled: the CEO and the PM (the API refuses it to others) */
export function useGitLabStatus(enabled = true) {
  return useQuery({
    queryKey: gitlabKeys.status(),
    queryFn: gitlabApi.getGitLabStatus,
    enabled,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Fetch GitLab projects
 */
export function useGitLabProjects(page: number = 1, perPage: number = 20) {
  return useQuery({
    queryKey: gitlabKeys.projectList(page, perPage),
    queryFn: () => gitlabApi.getGitLabProjects(page, perPage),
  });
}

/**
 * Full sync mutation
 */
export function useFullSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (options?: { since?: string; commitLimit?: number }) =>
      gitlabApi.triggerFullSync(options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitlabKeys.status() });
      queryClient.invalidateQueries({ queryKey: ['repositories'] });
      queryClient.invalidateQueries({ queryKey: ['commits'] });
    },
  });
}

/**
 * Sync repositories only mutation
 */
export function useSyncRepositories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: gitlabApi.syncRepositories,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitlabKeys.status() });
      queryClient.invalidateQueries({ queryKey: ['repositories'] });
    },
  });
}

/**
 * Sync users mutation
 */
export function useSyncUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: gitlabApi.syncUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gitlabKeys.status() });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
